import { ThunkDispatch } from "core-integration/src/store";
import {
  getCurrentEvent,
  getEventsById,
  getNextEvent,
} from "core-integration/src/store/events/reducers";
import { getGroupedFixturesByEvent } from "core-integration/src/store/fixtures/reducers";
import { fetchFixtures } from "core-integration/src/store/fixtures/thunks";
import {
  formatRawAsISO,
  formatRawAsLocal,
} from "core-integration/src/utils/datetime";
import { format } from "date-fns";
import { useEffect, useState } from "react";
import { DisclosureGroup } from "react-aria-components";
import { useDispatch, useSelector } from "react-redux";
import { usePlosTranslations } from "../../contexts/PlosTranslationsContext";
import AddToCalendar from "../../img/icons/add-to-calendar.svg?react";
import { PagerButton } from "../buttons/PagerButton";
import { deadline } from "../DeadlineBar/deadlineBar.css";
import { Fixture } from "./Fixture";
import FixtureWithStats from "./Fixture/FixtureWithStats";
import {
  addToCalendarButton,
  eventContainer,
  eventsDates,
  eventTitle,
  fixtureListItem,
  fixturesButton,
  fixturesContainer,
  fixturesHeader,
  fixturesList,
  fixturesPager,
  fixturesTitle,
  note,
} from "./fixtures.css";
import { FixturesProps } from "./types";

const Fixtures = ({
  eventId: initialEventId = 0,
  hideTitle = false,
  hideStats = false,
}: FixturesProps) => {
  const { fixtures, previousGameweek, nextGameweek } = usePlosTranslations();
  const dispatch = useDispatch<ThunkDispatch>();

  const currentEvent = useSelector(getCurrentEvent);

  const eventsById = useSelector(getEventsById);
  const groupedFixturesByEvent = useSelector(getGroupedFixturesByEvent);
  const nextEvent = useSelector(getNextEvent);

  const [eventId, setEventId] = useState(() => {
    if (initialEventId) {
      return initialEventId;
    }
    if (currentEvent && !currentEvent.finished) {
      // Current gw not finished
      return currentEvent.id;
    } else if (nextEvent) {
      // Next gw
      return nextEvent.id;
    } else if (currentEvent) {
      // Current gw. This will only happen once the last gw of the season
      // has finished
      return currentEvent.id;
    }
    return 0;
  });

  if (!eventId) {
    return;
  }

  useEffect(() => {
    dispatch(fetchFixtures(eventId));
  }, [dispatch, eventId]);

  const changeEvent = (newEventId: number) => {
    setEventId(newEventId);
  };

  const event = eventsById[eventId];
  if (!event) {
    return null;
  }

  const eventFixtureGroups = groupedFixturesByEvent[eventId] || [];

  const noPreviousEvent = !eventsById[eventId - 1];
  const noNextEvent = !eventsById[eventId + 1];

  const dateRangeText =
    eventFixtureGroups.length > 0 &&
    eventFixtureGroups[0].date.toDateString() !==
      eventFixtureGroups[eventFixtureGroups.length - 1].date.toDateString()
      ? ` - ${format(
          eventFixtureGroups[eventFixtureGroups.length - 1].date,
          "E d MMM",
        )}`
      : "";
  const eventDeadlineFormat = "E d MMM";
  const eventDeadlineDateFormat = "E d MMM, HH:mm";
  return (
    <div className={fixturesContainer}>
      <header className={fixturesHeader}>
        {!hideTitle && <h2 className={fixturesTitle}>{fixtures.fixtures}</h2>}
        <a
          href="https://pl.ecal.com/"
          target="_blank"
          rel="noopener noreferrer"
          role="button"
          // TODO: Remove addToCalendarButtonStyles and replace appropriately
          className={addToCalendarButton}
        >
          <AddToCalendar
            className={fixturesButton}
            aria-label={fixtures.addFixturesToCalendar}
          />
        </a>
      </header>

      <div className={eventContainer}>
        <h3 aria-live="polite" className={eventTitle}>
          {event.name}
        </h3>
        <time
          dateTime={formatRawAsISO(event.deadline_time)}
          className={eventsDates}
        >
          {formatRawAsLocal(event.deadline_time, eventDeadlineFormat)}
          {dateRangeText}
        </time>
      </div>

      <div className={fixturesPager}>
        <PagerButton
          isDisabled={noPreviousEvent}
          onPress={() => changeEvent(eventId - 1)}
          direction="left"
          label={previousGameweek}
        />
        <PagerButton
          isDisabled={noNextEvent}
          onPress={() => changeEvent(eventId + 1)}
          direction="right"
          label={nextGameweek}
        />
      </div>

      <div className={eventContainer}>
        <h4 className={deadline}>
          {fixtures.deadline(
            formatRawAsLocal(event.deadline_time, eventDeadlineDateFormat),
            { dateTime: formatRawAsISO(event.deadline_time) },
          )}
        </h4>
      </div>

      <p className={note}>{fixtures.deadlineNote}</p>
      {eventFixtureGroups.map((group) => (
        <div key={group.date.toISOString()}>
          <h4 className={eventTitle}>
            <time dateTime={group.date.toISOString()}>
              {format(group.date, eventDeadlineFormat)}
            </time>
          </h4>
          <ul className={fixturesList}>
            <DisclosureGroup>
              {group.fixtures.map((f) => (
                <li key={f.id} className={fixtureListItem}>
                  {hideStats ? (
                    <Fixture fixture={f} />
                  ) : (
                    <FixtureWithStats fixture={f} />
                  )}
                </li>
              ))}
            </DisclosureGroup>
          </ul>
        </div>
      ))}
    </div>
  );
};

export default Fixtures;
